import { Editor, Transforms, Element as SlateElement, Range } from "slate";
import {
  ELEMENT_PARAGRAPH,
  ELEMENT_LIST_ITEM,
  ELEMENT_NUMBERED_LIST,
  ELEMENT_BULLETED_LIST,
  getNewParagraph,
  ELEMENT_LINK,
} from "./Element";

export const LIST_TYPES = [ELEMENT_NUMBERED_LIST, ELEMENT_BULLETED_LIST];
export const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"];

export const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? "align" : "type");
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });

  let newProperties;

  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? ELEMENT_PARAGRAPH : isList ? ELEMENT_LIST_ITEM : format,
    };
  }
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = {
      type: format,
      children: [],
    };
    Transforms.wrapNodes(editor, block);
  }
};

export function isBlockType(editor, type) {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === type,
    }),
  );

  return !!match;
}

export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const isBlockActive = (editor, format, blockType = "type") => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n[blockType] === format,
    }),
  );

  return !!match;
};

export const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export function setOrInsertElement(editor, node) {
  if (isEmptyParagraph(editor)) {
    Transforms.setNodes(editor, node);
  } else {
    Transforms.insertNodes(editor, node);
  }

  Transforms.insertNodes(editor, getNewParagraph());
}

function isEmptyParagraph(editor) {
  if (
    editor.children.length === 1 &&
    editor.children[0].type === "paragraph" &&
    editor.children[0].children[0].text === ""
  ) {
    return true;
  }

  return false;
}

export function isLinkActive(editor) {
  const [link] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === ELEMENT_LINK,
  });
  return !!link;
}

export function unwrapLink(editor) {
  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link",
  });
}

export function wrapLink(editor, url) {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link = {
    type: ELEMENT_LINK,
    url,
    children: isCollapsed
      ? [
          {
            text: url,
          },
        ]
      : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, {
      split: true,
    });
    Transforms.collapse(editor, {
      edge: "end",
    });
  }
}

export function insertLink(editor, url) {
  if (editor.selection) {
    wrapLink(editor, url);
  }
}
